import { AxiosResponse } from 'axios';
import { httpClient } from './httpClient';

const UI_ASSETS_BASE_URL = '/ui-assets/access/configurations/';

export class ManifestService {
  private readonly baseConfigUrl: string;

  get isUiAssets(): boolean {
    return this.manifest.storage === 'UI_ASSETS';
  }

  constructor(private manifest: any, private lang: string, baseUrl = '') {
    this.baseConfigUrl = baseUrl;
  }

  static getV2Manifest(manifestPath: string): any {
    return httpClient.client
      .get(`${UI_ASSETS_BASE_URL}${manifestPath}`)
      .then(({ data }) => data);
  }

  // fallback for migrated v2 configs with partially missing data
  private configStructureFallback(configName: string) {
    const config = configName.replace('Config', '');
    const localePrefix = config === 'rrp' ? 'rrp' : `${config}Config`;

    return {
      config: `${config}Config.json`,
      ui: `${config}.ui.config.json`,
      translations: {
        [this.lang]: `${localePrefix}.${this.lang}.json`,
      },
    };
  }

  private getUiAssetsURLS(configName: string) {
    const target = this.manifest[configName] || this.configStructureFallback(configName);

    const localeUrl = `${UI_ASSETS_BASE_URL}${this.manifest.basePath}/${this.manifest.version}/${configName}/translations/${target.translations[this.lang]}`;
    const targetConfig = `${UI_ASSETS_BASE_URL}${this.manifest.basePath}/${this.manifest.version}/${configName}/${target.config}`;
    const uiConfigs = `${UI_ASSETS_BASE_URL}${this.manifest.basePath}/${this.manifest.version}/${configName}/${target?.ui || ''}`;

    return { localeUrl, targetConfig, uiConfigs };
  }

  private getLocalAssetsURLS(configName: string, language = this.lang) {
    const target = this.manifest[configName];

    const localeUrl = target.translations[language] && `${this.baseConfigUrl}${this.manifest.basePath}/${configName}/translations/${target.translations[language]}`;
    const targetConfig = `${this.baseConfigUrl}${this.manifest.basePath}/${configName}/${target.config}`;
    const uiConfigs = `${this.baseConfigUrl}${this.manifest.basePath}/${configName}/${target?.ui || ''}`;

    return { localeUrl, targetConfig, uiConfigs };
  }

  private getOriginalAssetsURLS(configName: string, language = this.lang) {
    const target = this.manifest[configName];

    const localeUrl = target.translations[language] && (this.baseConfigUrl + target.translations[language]);
    const targetConfig = this.baseConfigUrl + target.config;
    const uiConfigs = this.baseConfigUrl + (target?.ui || '');

    return { localeUrl, targetConfig, uiConfigs };
  }

  getAssetsURLS(configName: string, language?: string) {
    if (this.isUiAssets) {
      return this.getUiAssetsURLS(configName);
    }

    return import.meta.env.VITE_LOCAL_CONFIG_SERVER_ENDPOINT ? this.getLocalAssetsURLS(configName, language) : this.getOriginalAssetsURLS(configName, language);
  }

  private async getAssets(target: string) {
    return httpClient.client
      .get(target, {
        transformRequest: (data, headers) => {
          if (headers?.Authorization) { delete headers.Authorization; }
        },
      });
  }

  private getAssetsArray(assets: string[]) {
    const promiseArray: Promise<any>[] = assets.map(this.getAssets.bind(this));

    return Promise.all(promiseArray);
  }

  public getAppConfigs(configName = 'accessConfig') {
    const { localeUrl, targetConfig, uiConfigs } = this.getAssetsURLS(configName);
    const urls = [localeUrl, targetConfig];

    if (uiConfigs) { urls.push(uiConfigs); }

    return this.getAssetsArray(urls)
      .then((resultArray: AxiosResponse<any>[]) => ({
        locale: resultArray[0].data,
        workflow: resultArray[1].data,
        ui: resultArray[2]?.data,
      }));
  }

  public get rrpConfigs(): any {
    const { localeUrl, targetConfig, uiConfigs } = this.getAssetsURLS('rrpConfig');
    const urls = [localeUrl, targetConfig];

    if (uiConfigs) { urls.push(uiConfigs); }

    return this.getAssetsArray(urls)
      .then((resultArray: AxiosResponse<any>[]) => ({
        locale: resultArray[0].data,
        rrp: resultArray[1].data,
        ui: resultArray[2]?.data || '',
      })).catch(e => {
        console.log('e', e);
      });
  }

  public getRrpByLocale(language?: string) {
    const { localeUrl } = this.getAssetsURLS('rrpConfig', language);

    if (!localeUrl) {
      throw new Error('This language is not defined in the manifest');
    }

    return this.getAssets(localeUrl)
      .then((result: AxiosResponse<any>) => (result.data)).catch(e => {
        console.log(`GetAssets failed for ${localeUrl}`, e);
        throw new Error(`GetAssets failed for ${localeUrl}`);
      });
  }

  public get globalStoredInUIAssets(): boolean {
    return !this.manifest?.global?.translations;
  }
}
